if (window.location.hostname === 'app.8ad.io') {
  window.location.href = window.location.href.replace('://app.8ad.io', '://app.infinite.ad');
}

import './assets/ui.css';
import './assets/icons.css';

/* IMPORT SCRIPTS */
import { InfiniteUi } from '@infinite-tools/ui';
import * as Sentry from '@sentry/vue';
import { AxiosError } from 'axios';
import { createApp, h } from 'vue';
import VueTelInput from 'vue-tel-input';

import App from '@/App.vue';
import { NonCriticalError, ValidationError } from '@/errors';
import ConditionalFilteredList from '@/mixins/ConditionalFilteredList';
import ErrorNotifier from '@/mixins/ErrorNotifier';
import Translator from '@/mixins/Translator';
import FullscreenModal from '@/plugins/FullscreenModal';
import ConfirmationModal from '@/plugins/confirmation';
import ErrorModal from '@/plugins/error-modal';
import { router } from '@/router';
import { store } from '@/store';
import AccessHandledRouterLink from '@/ui/components/AccessHandledRouterLink.vue';
import TooltipDirective from '@/ui/directives/TooltipDirective.js';
import ToastPlugin from '@/ui/plugins/ToastPlugin.js';
import { setAppInstance } from '@/utils/appInstance.js';
import { saveUrlParameters } from '@/utils/storage.js';

saveUrlParameters();

const app = createApp({
  data: () => ({
    screenWidth: window.innerWidth,
  }),
  computed: {
    language() {
      return store.getters['app/language'];
    },
    isUserFetching() {
      return this.$store.getters['auth/isFetching'];
    },
    isLoggedIn() {
      return this.$store.getters['auth/loggedIn'];
    },
    hasUserToken() {
      return this.$store.getters['auth/hasToken'];
    },
    loggedInUser() {
      return store.getters['auth/user'];
    },
    currentCompany() {
      return store.getters['auth/company'];
    },
    isInOriginalCompany() {
      return this.loggedInUser?.company?.id === this.currentCompany?.id;
    },
    isSupportMode() {
      return process.env.VUE_APP_SUPPORT_BUILD == 1;
    },
    loggedInSupportUser() {
      return store.getters['support/user'];
    },
    supportHasL2Access() {
      const supportLevel = this.loggedInSupportUser ? this.loggedInSupportUser.level : null;
      return this.isSupportMode && supportLevel >= 2;
    },
    supportHasL3Access() {
      const supportLevel = this.loggedInSupportUser ? this.loggedInSupportUser.level : null;
      return this.isSupportMode && supportLevel >= 3;
    },
    isMobile() {
      return this.screenWidth <= 768;
    },
  },
  created() {
    this.updateScreenWidth();
    window.addEventListener('resize', () => this.screenWidth = window.innerWidth);

    if (this.isSupportMode) {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('user_id');

      if (userId) {
        this.$store.commit('support/setAutoLoginUserId', userId);

        if (this.hasUserToken) {
          this.$store.dispatch('auth/logout');
        }
      }
    }
  },
  mounted() {
    this.updateScreenWidth();
    setTimeout(() => this.updateScreenWidth(), 250);

    this.initInputAutoTrimmer();
  },
  methods: {
    initInputAutoTrimmer() {
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.addedNodes.length === 0) {
            return;
          }

          mutation.addedNodes.forEach(node => {
            if (!(node instanceof Element)) {
              return;
            }

            if (node instanceof HTMLInputElement) {
              node.removeEventListener('blur', this.inputAutoTrim);
              node.addEventListener('blur', this.inputAutoTrim);
            }
            else {
              const inputs = node.querySelectorAll('input');
              inputs.forEach(input => {
                input.removeEventListener('blur', this.inputAutoTrim);
                input.addEventListener('blur', this.inputAutoTrim);
              });
            }
          });
        });
      });

      observer.observe(document.body, { subtree: true, childList: true });
    },
    inputAutoTrim(e) {
      const ariaControlsValue = e.target.getAttribute('aria-controls');
      if (typeof ariaControlsValue === 'string' && ariaControlsValue.indexOf('autosuggest') !== -1) {
        return;
      }

      this.$nextTick(() => {
        // Only when trimming changed something: a synthetic input event on an untouched
        // field is taken as typing by some widgets (e.g. the pin input jumps to the next slot).
        const trimmed = e.target.value.trim();
        if (trimmed === e.target.value) {
          return;
        }

        e.target.value = trimmed;
        e.target.dispatchEvent(new Event('input', { bubbles: true }));
      });
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    fetchUserData() {
      return this.$store.dispatch('auth/fetch');
    },
    fetchSupportUserData() {
      this.$store.commit('support/isFetching', true);

      return this.$store.dispatch('support/fetch')
        .finally(async () => {
          await this.$nextTick();
          this.$store.commit('support/isFetching', false);
        });
    },
  },
  render: () => h(App),
});

/* PLUGINS & GLOBAL REGISTRATIONS (Vue 3 app instance API) */
app.use(router);
app.use(store);

app.component('AccessHandledRouterLink', AccessHandledRouterLink);

app.use(FullscreenModal);
app.use(ConfirmationModal);
app.use(ErrorModal);
app.directive('tooltip', TooltipDirective);
app.use(ToastPlugin);
app.use(VueTelInput);
app.use(InfiniteUi);

app.mixin(Translator);
app.mixin(ConditionalFilteredList);
app.mixin(ErrorNotifier);

app.config.globalProperties.$store = store;
app.config.globalProperties.$app = {
  isShoprenterApp: process.env.VUE_APP_FORK === 'shoprenter',
};

if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'beta') {
  Sentry.init({
    app,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    ignoreErrors: [
      /Redirected when going from "(.*?)" to "(.*?)" via a navigation guard/,
      /Avoided redundant navigation to current location: (.*?)/,

      // third-party
      /linkedin\.com/,
      /platform\.linkedin/,
      /googletagmanager\.com/,
      /gtm\.js/,
      /clarity\.ms/,
      /^Non-Error promise rejection/,
      'ResizeObserver loop limit exceeded',
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (
        error instanceof NonCriticalError
        || (error instanceof AxiosError && error.code === AxiosError.ERR_NETWORK)
      ) {
        return null;
      }

      return event;
    },
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      /^https:\/\/app\.infinite\.ad/,
      /^https:\/\/beta\.app\.8ad\.io/,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

const mountApp = () => {
  const appInstance = app.mount('#app');
  setAppInstance(appInstance);

  ValidationError.appInstance = appInstance;

  if (process.env.NODE_ENV !== 'production') {
    window.AppInstance = appInstance;
  }
};

mountApp();
